import type { DeviceModelFeature } from '../../services/admin';

/** Feature codes of the server's app_features registry (migration 0065). */
export const FEATURE = {
  sos: 'sos',
  fallDetection: 'fall_detection',
  secondaryGroup: 'secondary_group',
  directCall: 'direct_call',
  messages: 'messages',
  status: 'status',
} as const;

/** Per-radio setting fields that belong to each capability-controlled feature. */
const FEATURE_FIELDS: Record<string, string[]> = {
  [FEATURE.sos]: ['sos_enabled'],
  [FEATURE.fallDetection]: ['fall_detection_enabled'],
  [FEATURE.secondaryGroup]: ['secondary_group_enabled'],
  [FEATURE.directCall]: ['direct_call_enabled'],
  [FEATURE.messages]: ['messages_enabled'],
  [FEATURE.status]: ['status_enabled'],
};

/** Whether any per-radio setting is hidden because the model lacks its feature (hardware-only
 *  capabilities with their own box, such as dmr, do not count). */
export function hasHiddenFeatureSettings(radio: { unsupported_features?: string[] | null }): boolean {
  return (radio.unsupported_features ?? []).some((code) => code in FEATURE_FIELDS);
}

/** Whether the radio's canonical model supports the feature. Unknown/new radios support all. */
export function supportsFeature(radio: { unsupported_features?: string[] | null }, code: string): boolean {
  return !(radio.unsupported_features ?? []).includes(code);
}

/**
 * Drops the setting fields of features the model does not support, so a normal full-form save
 * never asks the server to enable one (the server would answer 409 FEATURE_NOT_SUPPORTED) and the
 * radio's stored preference is left as it is.
 */
export function omitUnsupportedFields<T extends Record<string, unknown>>(payload: T, radio: { unsupported_features?: string[] | null }): T {
  const out: Record<string, unknown> = { ...payload };
  for (const code of radio.unsupported_features ?? []) {
    for (const field of FEATURE_FIELDS[code] ?? []) delete out[field];
  }
  return out as T;
}

/** Editor rows for a model's capability list (server order). */
export function toFeatureRows(features: DeviceModelFeature[] | null | undefined): DeviceModelFeature[] {
  return (features ?? []).map((f) => ({ code: f.code, name: f.name ?? f.code, supported: f.supported }));
}

/** What a model save sends: code + supported only. */
export function featurePayload(rows: DeviceModelFeature[]): DeviceModelFeature[] {
  return rows.map((r) => ({ code: r.code, supported: r.supported }));
}
